import React, { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { StandardPage, StandardForm, ApiService } from '../../shared'
import ExperienceForm from './components/ExperienceForm'
import CompetencyForm from './components/CompetencyForm'
import { useExperienceForm } from './hooks/useExperienceForm'
import { useCompetencyForm } from './hooks/useCompetencyForm'
import { applyDateMask, validateDate } from './utils/dateUtils'
import { validateCurriculumForm } from './utils/validation'
import { CURSOS_GRADUACAO } from './constants/cursosGraduacao'

const EMPTY_LIST = []

// "DD/MM/YYYY" ⇄ "YYYY-MM-DD" by string, never via Date (no timezone drift).
const formatDateForFrontend = (dateString) => {
  if (!dateString) return ''
  const [year, month, day] = String(dateString).slice(0, 10).split('-')
  return year && month && day ? `${day}/${month}/${year}` : ''
}

const formatDateForBackend = (dateString) => {
  if (!dateString || !validateDate(dateString)) return null
  const [day, month, year] = dateString.split('/')
  return `${year}-${month}-${day}`
}

const CurriculumV2 = () => {
  const { t } = useTranslation()
  const [showCollegeField, setShowCollegeField] = useState(false)
  const [showCustomCourse, setShowCustomCourse] = useState(false)
  const [showStudyDetails, setShowStudyDetails] = useState(false)
  const [showEndDate, setShowEndDate] = useState(false)
  const [showStudyPeriod, setShowStudyPeriod] = useState(false)
  const [curriculumData, setCurriculumData] = useState(null)

  // Hooks live here (not inside renderForm) so React's rules of hooks hold.
  const experienceHook = useExperienceForm(t, curriculumData?.experiences || EMPTY_LIST)
  const competencyHook = useCompetencyForm(t, curriculumData?.competencies || EMPTY_LIST)

  // Lists as of the last validate() call — includes anything auto-added on save.
  const listsRef = useRef({ experiences: null, competencies: null })

  const curriculumFields = [
    { name: 'aboutMe', label: 'About me', type: 'textarea', props: { rows: 4, maxLength: 500 } },
    {
      name: 'educationLevel', label: 'Education Level', type: 'select',
      options: [
        { value: 'elementary', label: 'Elementary School' },
        { value: 'highschool', label: 'High School' },
        { value: 'college', label: 'College' },
        { value: 'graduate', label: 'Graduate School' }
      ]
    },
    { name: 'collegeName', label: 'Instituição de Ensino', type: 'text', placeholder: 'Nome da instituição', required: true },
    { name: 'courseName', label: 'Curso', type: 'searchable-select', placeholder: 'Digite para buscar o curso',
      options: [
        ...CURSOS_GRADUACAO.map(c => ({ value: c, label: c })),
        { value: 'Outro', label: 'Outro' }
      ]
    },
    { name: 'courseNameCustom', label: 'Nome do Curso', type: 'text', placeholder: 'Descreva o nome do curso' },
    {
      name: 'studyStatus', label: 'Study Status', type: 'select', placeholder: 'Select study status', required: true,
      options: [
        { value: 'studying', label: 'Studying' },
        { value: 'finished', label: 'Finished' }
      ]
    },
    { name: 'studyStartDate', label: 'Study Start Date', type: 'text', placeholder: 'DD/MM/AAAA', required: true, props: { inputMode: 'numeric', maxLength: 10 } },
    { name: 'studyEndDate', label: 'Study End Date', type: 'text', placeholder: 'DD/MM/AAAA', required: true, props: { inputMode: 'numeric', maxLength: 10 } },
    {
      name: 'studyPeriod', label: 'Study Period', type: 'select', placeholder: 'Select study period', required: true,
      options: [
        { value: 'morning', label: 'Morning' },
        { value: 'afternoon', label: 'Afternoon' },
        { value: 'evening', label: 'Evening' }
      ]
    }
  ]

  const transformApiData = (data) => {
    if (!data) return {}
    setCurriculumData(data)
    const isHigherEducation = ['college', 'graduate'].includes(data.educationLevel)
    setShowCollegeField(isHigherEducation)
    setShowCustomCourse(data.courseName === 'Outro')
    setShowStudyDetails(isHigherEducation)
    setShowEndDate(data.studyStatus === 'finished')
    setShowStudyPeriod(data.studyStatus === 'studying')
    return {
      aboutMe: data.aboutMe || '',
      educationLevel: data.educationLevel || '',
      collegeName: data.collegeName || '',
      courseName: data.courseName || '',
      courseNameCustom: data.courseNameCustom || '',
      studyStatus: data.studyStatus || '',
      studyStartDate: formatDateForFrontend(data.studyStartDate),
      studyEndDate: formatDateForFrontend(data.studyEndDate),
      studyPeriod: data.studyPeriod || ''
    }
  }

  /**
   * Runs before submit. Besides the field rules, it protects the user from
   * the #1 data-loss path: an experience/competency typed but never "added".
   * If the pending sub-form is valid it is added automatically; if not, the
   * save is blocked and the user is taken to it.
   */
  const validate = useCallback((values) => {
    const errors = validateCurriculumForm(values)
    if (!values.educationLevel) errors.educationLevel = t('Education level is required')

    let experiences = experienceHook.experiences
    if (experienceHook.hasPendingInput()) {
      const r = experienceHook.addExperience()
      if (r.ok) experiences = r.experiences
      else errors._experience = t('You have an experience in progress. Complete it or clear it before saving.')
    }

    let competencies = competencyHook.competencies
    if (competencyHook.hasPendingInput()) {
      const r = competencyHook.addCompetency()
      if (r.ok) competencies = r.competencies
      else errors._competency = t('You have a competency in progress. Complete it or clear it before saving.')
    }

    listsRef.current = { experiences, competencies }
    return errors
  }, [t, experienceHook, competencyHook])

  const submitData = (formData) => {
    const experiences = (listsRef.current.experiences ?? experienceHook.experiences).map(exp => ({
      company: exp.company,
      position: exp.position,
      startDate: formatDateForBackend(exp.startDate),
      endDate: exp.isCurrentJob ? null : formatDateForBackend(exp.endDate),
      isCurrentJob: !!exp.isCurrentJob,
      activities: exp.activities || null
    }))
    const competencies = (listsRef.current.competencies ?? competencyHook.competencies).map(comp => ({
      name: comp.name,
      customName: comp.customName || null,
      level: comp.level,
      description: comp.description || null
    }))

    const isHigherEducation = ['college', 'graduate'].includes(formData.educationLevel)
    const dataToSubmit = {
      aboutMe: formData.aboutMe || null,
      educationLevel: formData.educationLevel,
      collegeName: isHigherEducation ? formData.collegeName : null,
      courseName: isHigherEducation ? formData.courseName : null,
      courseNameCustom: isHigherEducation && formData.courseName === 'Outro' ? formData.courseNameCustom : null,
      studyStatus: isHigherEducation ? formData.studyStatus : null,
      studyStartDate: isHigherEducation ? formatDateForBackend(formData.studyStartDate) : null,
      studyEndDate: isHigherEducation && formData.studyStatus === 'finished' ? formatDateForBackend(formData.studyEndDate) : null,
      studyPeriod: isHigherEducation && formData.studyStatus === 'studying' ? formData.studyPeriod : null,
      experiences,
      competencies
    }

    listsRef.current = { experiences: null, competencies: null }
    return ApiService.updateCurriculum(dataToSubmit)
  }

  const renderForm = ({ values, errors, handleChange }) => {
    const handleEducationChange = (e) => {
      handleChange(e)
      const isHigherEducation = ['college', 'graduate'].includes(e.target.value)
      setShowCollegeField(isHigherEducation)
      setShowStudyDetails(isHigherEducation)

      if (!isHigherEducation) {
        ;['collegeName', 'courseName', 'courseNameCustom', 'studyStatus', 'studyStartDate', 'studyEndDate', 'studyPeriod']
          .forEach(name => handleChange({ target: { name, value: '' } }))
        setShowEndDate(false)
        setShowStudyPeriod(false)
      }
    }

    const handleStudyStatusChange = (e) => {
      handleChange(e)
      const isFinished = e.target.value === 'finished'
      const isStudying = e.target.value === 'studying'
      setShowEndDate(isFinished)
      setShowStudyPeriod(isStudying)
      if (isFinished) handleChange({ target: { name: 'studyPeriod', value: '' } })
      else if (isStudying) handleChange({ target: { name: 'studyEndDate', value: '' } })
    }

    return (
      <>
        <StandardForm
          title="About me"
          description="Tell us about your background and education."
          fields={curriculumFields.filter(field => {
            if (['aboutMe', 'educationLevel'].includes(field.name)) return true
            if (['collegeName', 'courseName', 'studyStatus', 'studyStartDate'].includes(field.name)) return showCollegeField
            if (field.name === 'courseNameCustom') return showCollegeField && showCustomCourse
            if (field.name === 'studyEndDate') return showCollegeField && showEndDate
            if (field.name === 'studyPeriod') return showCollegeField && showStudyPeriod
            return false
          }).map(field => {
            if (field.name === 'educationLevel') return { ...field, onChange: handleEducationChange, required: true }
            if (field.name === 'courseName') {
              return { ...field, onChange: (e) => {
                handleChange(e)
                const isOutro = e.target.value === 'Outro'
                setShowCustomCourse(isOutro)
                if (!isOutro) handleChange({ target: { name: 'courseNameCustom', value: '' } })
              }}
            }
            if (field.name === 'studyStatus') return { ...field, onChange: handleStudyStatusChange }
            if (['studyStartDate', 'studyEndDate'].includes(field.name)) {
              return {
                ...field,
                onChange: (e) => handleChange({ target: { name: e.target.name, value: applyDateMask(e.target.value) } })
              }
            }
            return field
          })}
          values={values}
          errors={errors}
          handleChange={handleChange}
          t={t}
        />

        <ExperienceForm
          experience={experienceHook.experience}
          experiences={experienceHook.experiences}
          errors={experienceHook.errors}
          pendingError={errors._experience}
          handleExperienceChange={experienceHook.handleExperienceChange}
          addExperience={experienceHook.addExperience}
          removeExperience={experienceHook.removeExperience}
          clearPending={experienceHook.clearPending}
          t={t}
        />

        <CompetencyForm
          competency={competencyHook.competency}
          competencies={competencyHook.competencies}
          errors={competencyHook.errors}
          pendingError={errors._competency}
          handleCompetencyChange={competencyHook.handleCompetencyChange}
          addCompetency={competencyHook.addCompetency}
          removeCompetency={competencyHook.removeCompetency}
          clearPending={competencyHook.clearPending}
          t={t}
        />
      </>
    )
  }

  return (
    <StandardPage
      title="Home"
      breadcrumbItem="My Curriculum"
      fetchData={ApiService.getCurriculum}
      submitData={submitData}
      transformData={transformApiData}
      validate={validate}
      renderForm={renderForm}
    />
  )
}

export default CurriculumV2
